import { MainLayout } from '../../Layout/MainLayout';
import {
  AccountListItem,
  BrightyListItem,
  Button,
  DesignField,
  DetailsListItem,
  FormContentIsland,
  Grid,
  HookForm,
  ListContentIsland,
  Money,
  Stack,
  Tab,
  TabsField,
} from '@razz-team/ui-web';
import { useForm } from 'react-hook-form';
import { Card } from './Card';
import {
  useCardDesignListQuery,
  useCardIntentQuery,
  useCardOrderMutation,
} from '../../../api/cards';
import { ApiCardOrderChallenge, ApiCardOrderIntentRequest } from '../../../openapi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccountField } from '../../Field/AccountField';
import { useAccountsQuery, useCardLinkableCurrenciesQuery } from '../../../api/accounts';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useFormError } from '../../../hooks/useFormError';
import { Route } from '../../../routes/account.cards_.create';
import { ChallengeRequired } from '../TransferPage/TransferChallenge';
import { useCustomerQuery, useCustomerSettingsQuery } from '../../../api/customers';
import { MemberField } from '../../Field/MemberField';
import { useTranslation } from 'react-i18next';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';

interface CreateCardFormValues {
  formFactor: 'PLASTIC' | 'VIRTUAL';
  cardDesignId: string;
  cardName?: null | string;
  customerId?: null | string;
  deliveryMethod?: null | string;
  holderName?: null | string;
  solution?: null | string;
  sourceAccountId: string;
}

export function CreateCardPage() {
  const { t } = useTranslation();
  const [intentRequest, setIntentRequest] = useState<ApiCardOrderIntentRequest | undefined>();
  const accountsQuery = useAccountsQuery();
  const cardLinkableCurrenciesQuery = useCardLinkableCurrenciesQuery();
  const customerSettingsQuery = useCustomerSettingsQuery();
  const designListQuery = useCardDesignListQuery();
  const intentQuery = useCardIntentQuery(intentRequest);
  const idempotencyKey = useIdempotencyKey();
  const orderMutation = useCardOrderMutation(idempotencyKey);
  const customerQuery = useCustomerQuery();
  const [challenge, setChallenge] = useState<ApiCardOrderChallenge | undefined>();

  const navigate = Route.useNavigate();

  useEffect(() => {
    if (orderMutation.isIdle) {
      setChallenge(undefined);
    } else if (orderMutation.data) {
      if (orderMutation.data.type === 'CHALLENGES_COMPLETED') {
        navigate({ to: '/account/cards/$cardId', params: { cardId: orderMutation.data.card.id } });
      } else {
        setChallenge(orderMutation.data);
      }
    }
  }, [navigate, orderMutation.data, orderMutation.isIdle]);

  const form = useForm<CreateCardFormValues>({
    defaultValues: {
      formFactor: 'VIRTUAL',
    },
  });

  const [formFactor, holderName, cardDesignId, sourceAccountId, customerId] = form.watch([
    'formFactor',
    'holderName',
    'cardDesignId',
    'sourceAccountId',
    'customerId',
  ]);

  useFormError(form, 'formFactor', designListQuery.error);
  useFormError(form, 'sourceAccountId', accountsQuery.error ?? orderMutation.error);
  useFormError(form, 'customerId', intentQuery.error ?? customerQuery.error);
  useFormError(form, 'solution', orderMutation.error);

  const accounts = accountsQuery.data;
  useEffect(() => {
    if (!sourceAccountId && accounts?.length) {
      form.setValue('sourceAccountId', accounts[0].id, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [sourceAccountId, accounts, form]);

  const intent = intentQuery.data;
  useEffect(() => {
    if (intent?.holderNameValidity.type === 'VALID') {
      form.setValue('holderName', intent.holderNameValidity.holderName, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [holderName, intent, form]);

  const profile = customerQuery.data?.profile;
  useEffect(() => {
    if (!customerId && profile) {
      form.setValue('customerId', profile.id, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [customerId, profile, form]);

  const products = customerSettingsQuery.data?.products;
  const cardDesigns = useMemo(
    () =>
      designListQuery.data
        ?.filter((it) => it.formFactor === formFactor)
        // well, we have to do that filtration instead of backend
        // remove once they will fix it
        .filter((it) => {
          switch (it.formFactor) {
            case 'VIRTUAL':
              return products?.virtualCardProduct?.conditions.some(
                (condition) => condition.id === it.conditionId,
              );
            case 'PLASTIC':
              return products?.physicalCardProduct?.conditions.some(
                (condition) => condition.id === it.conditionId,
              );
            default:
              return false;
          }
        }),
    [formFactor, designListQuery.data, products],
  );
  useEffect(() => {
    if (cardDesigns?.length && !cardDesigns.some((it) => it.id === cardDesignId)) {
      form.setValue('cardDesignId', cardDesigns[0].id, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [cardDesignId, cardDesigns, form]);

  useEffect(() => {
    const account = accounts?.find((account) => account.id === sourceAccountId);
    if (cardDesignId && account && customerId) {
      setIntentRequest({
        cardDesignId,
        customerId,
      });
    }
  }, [cardDesignId, accounts, holderName, sourceAccountId, customerId]);

  const handleSubmit = useCallback(
    (values: CreateCardFormValues) => {
      if (intent && customerId) {
        orderMutation.mutate({
          cardDesignId: values.cardDesignId,
          cardName: t('{{companyName}} card'),
          // deliveryMethod?: null | string;
          // address: intent.address,
          fees: intent.fees,
          hash: intent.hash,
          holderName: values.holderName,
          solution: values.solution,
          sourceAccountId: values.sourceAccountId,
          customerId,
        });
      }
    },
    [intent, orderMutation, customerId, t],
  );

  const selectedAccount = useMemo(
    () => accounts?.find((account) => account.id === sourceAccountId),
    [sourceAccountId, accounts],
  );

  const isPhysycalSupported = useMemo(
    () => designListQuery.data?.some((it) => it.formFactor === 'PLASTIC'),
    [designListQuery.data],
  );

  const cardDesign = useMemo(
    () => cardDesigns?.find((design) => design.id === cardDesignId),
    [cardDesignId, cardDesigns],
  );

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Open card')}
          parent={{
            to: '/account/cards',
            title: t('Cards'),
          }}
        />
      }
      compact
    >
      <Grid container component={HookForm} form={form} onSubmit={handleSubmit} size={12}>
        <Stack
          direction={{
            lg: 'row',
            md: 'column-reverse',
            sm: 'column-reverse',
            xs: 'column-reverse',
          }}
          spacing={4}
          width="100%"
        >
          <Stack spacing={4} width="100%">
            {challenge?.type === 'CHALLENGE_REQUIRED' ? (
              <>
                <ChallengeRequired
                  entity={t('card order')}
                  challenge={challenge}
                  actions={
                    <Button variant="text" onClick={orderMutation.reset}>
                      {t('Edit')}
                    </Button>
                  }
                />
                <ListContentIsland title={t('Basics')}>
                  {selectedAccount ? <AccountListItem account={selectedAccount} /> : null}
                  <BrightyListItem title={t('Cardholder')} subtitle={holderName} />
                </ListContentIsland>
              </>
            ) : (
              <FormContentIsland title={t('Basics')}>
                {isPhysycalSupported ? (
                  <TabsField name="formFactor" label={t('Card type')}>
                    <Tab label={t('Virtual')} value="VIRTUAL" />
                    <Tab label={t('Physical')} value="PHYSYCAL" />
                  </TabsField>
                ) : null}
                <AccountField
                  name="sourceAccountId"
                  label={t('Account')}
                  accounts={accountsQuery.data}
                  loading={accountsQuery.isPending}
                  currenciesWhitelist={cardLinkableCurrenciesQuery.data}
                  autoFocus
                />
                <MemberField name="customerId" label={t('Owner')} />
              </FormContentIsland>
            )}
            <Button
              type="submit"
              size="large"
              color="primary"
              loading={orderMutation.isPending}
              fullWidth
            >
              {t('Continue')}
            </Button>
          </Stack>
          <Stack gap={4}>
            <Card holderName={holderName} cardDesign={cardDesign} />
            <DesignField
              name="cardDesignId"
              options={cardDesigns}
              loading={designListQuery.isPending}
            />
            <ListContentIsland>
              <DetailsListItem label={t('Card Type')} value={cardDesign?.formFactor} />
              <DetailsListItem
                label={t('Issue Fee')}
                value={intent ? <Money value={intent?.amount} /> : null}
                loading={intentQuery.isPending}
              />
            </ListContentIsland>
          </Stack>
        </Stack>
      </Grid>
    </MainLayout>
  );
}
